import React, {Fragment} from "react";
import { connect } from 'react-redux'
import { AutoSizer, MultiGrid } from 'react-virtualized'
import 'react-virtualized/styles.css'
import { isNumber } from "util";
import AceEditor from 'react-ace';
import 'brace';
import 'brace/mode/mysql';
import 'brace/mode/sql';
import 'brace/theme/github';
import 'brace/ext/language_tools';
import Pagination from 'react-js-pagination'
import FontAwesome from 'react-fontawesome'

// Acciones
import { executeQuery, updateInputs } from "../actions/admindb";

// Templates de queries útiles para soporte
const QUERY_TEMPLATES = {
    '': '-- Seleccione un template',
    'usuario_por_email': '-- Buscar usuario por email\nSELECT * FROM usuarios WHERE email LIKE \'%@icesi.edu.co%\' LIMIT 50',
    'solicitudes_recientes': '-- Solicitudes creadas en los últimos 7 días\nSELECT id, proyecto_evento, estado, solicitante_id, created_at \nFROM solicitudes \nWHERE created_at >= DATE_SUB(NOW(), INTERVAL 7 DAY) \nORDER BY created_at DESC \nLIMIT 100',
    'documentos_por_estado': '-- Documentos por estado específico\nSELECT d.id, d.estado, d.solicitud_id, s.proyecto_evento \nFROM documentos d \nJOIN solicitudes s ON d.solicitud_id = s.id \nWHERE d.estado = 11 \nLIMIT 50',
    'facturas_por_documento': '-- Facturas de un documento específico\nSELECT f.*, d.solicitud_id \nFROM facturas f \nJOIN documentos d ON f.documento_id = d.id \nWHERE d.id = 12345',
    'verificar_totales': '-- Verificar totales documento vs facturas\nSELECT \n    d.id as documento_id,\n    SUM(dd.valor_con_iva * dd.cantidad) as total_detalles,\n    (SELECT SUM(f.valor_a_pagar) FROM facturas f WHERE f.documento_id = d.id) as total_facturas,\n    ABS(SUM(dd.valor_con_iva * dd.cantidad) - COALESCE((SELECT SUM(f.valor_a_pagar) FROM facturas f WHERE f.documento_id = d.id), 0)) as diferencia\nFROM documentos d\nLEFT JOIN documento_detalle dd ON d.id = dd.documento_id\nWHERE d.id = 12345\nGROUP BY d.id',
    'solicitud_completa': '-- Ver solicitud con documentos y facturas\nSELECT \n    s.id as solicitud_id,\n    s.proyecto_evento,\n    d.id as documento_id,\n    d.estado as doc_estado,\n    COUNT(f.id) as num_facturas\nFROM solicitudes s\nLEFT JOIN documentos d ON s.id = d.solicitud_id\nLEFT JOIN facturas f ON d.id = f.documento_id\nWHERE s.id = 12345\nGROUP BY s.id, s.proyecto_evento, d.id, d.estado'
};

export function findValues(c, x) {
    let value = x.getIn(c.VALUE.split('.'))
	return value
}

class AdminDB extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            readOnly: true,
            executionTime: 0,
            queryHistory: this.loadHistory(),
            showHistory: false,
            editorHeight: 150, // Altura inicial del editor
            isResizing: false
        };
        this.multiGridRef = React.createRef();
    }

    componentDidMount() {
        document.addEventListener('mousemove', this.handleMouseMove);
        document.addEventListener('mouseup', this.handleMouseUp);
    }

    componentWillUnmount() {
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.records != prevProps.records) {
            if (this.multiGridRef.current) {
                this.multiGridRef.current.forceUpdateGrids()
            }
        }
    }

    handleMouseDown = (e) => {
        this.setState({ isResizing: true });
        e.preventDefault();
    }

    handleMouseMove = (e) => {
        if (!this.state.isResizing) return;

        // Obtener la posición del editor
        const editorElement = document.querySelector('.ace_editor');
        if (!editorElement) return;

        const editorTop = editorElement.getBoundingClientRect().top;
        const newHeight = e.clientY - editorTop;

        // Limites: min 100px, max 600px
        if (newHeight >= 100 && newHeight <= 600) {
            this.setState({ editorHeight: newHeight });
        }
    }

    handleMouseUp = () => {
        if (this.state.isResizing) {
            this.setState({ isResizing: false });
        }
    }

    // Guardar en localStorage
    saveToHistory = (query, connection, records, executionTime) => {
        const history = this.loadHistory();
        const newEntry = {
            query,
            connection,
            records,
            executionTime,
            timestamp: new Date().toISOString()
        };

        // Mantener solo las últimas 20
        const updatedHistory = [newEntry, ...history].slice(0, 20);
        localStorage.setItem('admindb_history', JSON.stringify(updatedHistory));
        this.setState({ queryHistory: updatedHistory });
    }

    loadHistory = () => {
        try {
            const stored = localStorage.getItem('admindb_history');
            return stored ? JSON.parse(stored) : [];
        } catch (e) {
            return [];
        }
    }

    clearHistory = () => {
        localStorage.removeItem('admindb_history');
        this.setState({ queryHistory: [] });
    }

    loadQueryFromHistory = (query) => {
        this.props.updateInputs('query', query);
        this.setState({ showHistory: false });
    }

    loadTemplate = (e) => {
        const template = e.target.value;
        if (template && QUERY_TEMPLATES[template]) {
            this.props.updateInputs('query', QUERY_TEMPLATES[template]);
        }
    }

    exportToCSV = () => {
        const { data, columns, ids } = this.props;

        if (ids.count() === 0) {
            alert('No hay datos para exportar');
            return;
        }

        // Headers
        let csv = columns.map(c => `"${c.CAPTION}"`).join(',') + '\n';

        // Rows
        ids.forEach(id => {
            const row = data.get(String(id));
            const values = columns.map(c => {
                let value = findValues(c, row);
                // Escapar comillas y envolver en comillas
                value = value ? String(value).replace(/"/g, '""') : '';
                return `"${value}"`;
            });
            csv += values.join(',') + '\n';
        });

        // Descargar
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `query_results_${Date.now()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    copyToClipboard = () => {
        const { data, columns, ids } = this.props;

        if (ids.count() === 0) {
            alert('No hay datos para copiar');
            return;
        }

        // Formato TSV (Tab-Separated Values) para mejor compatibilidad con Excel
        let text = columns.map(c => c.CAPTION).join('\t') + '\n';

        ids.forEach(id => {
            const row = data.get(String(id));
            const values = columns.map(c => {
                let value = findValues(c, row);
                return value || '';
            });
            text += values.join('\t') + '\n';
        });

        // Copiar al clipboard
        navigator.clipboard.writeText(text).then(() => {
            alert('Datos copiados al portapapeles');
        }).catch(err => {
            console.error('Error al copiar:', err);
        });
    }

    cellRenderer = ({columnIndex, key, rowIndex, style}) => {
        if (rowIndex === 0) {
            let col = this.props.columns[columnIndex]
            return <div key={key} className="rvtableheader" style={{...style, textAlign: 'center'}}>{col.CAPTION}</div>
        } else {
            const ri = rowIndex - 1
            if (!isNumber(ri)) return null
            const id = this.props.ids.get(ri)
            const c = this.props.columns[columnIndex]
            const a = this.props.data.get(String(id))

            let alignment = 'left'
            let value = findValues(c, a)
            const fixed = c.FIXED
            return <div key={key} className={`rvtablecell${fixed ? ' fixed':''}`} style={{...style, textAlign: alignment }}><span>{value}</span></div>
        }
    }

    getColumnWidth = ({index}) => {
        const c = this.props.columns[index]
        return c.WIDTH || 150
    }

    onClick = (e) => {
        const callback = (result) => {
            if (this.multiGridRef.current) {
                this.multiGridRef.current.forceUpdateGrids();
            }

            // Guardar en historial si fue exitoso
            if (result && result.executionTime) {
                this.setState({ executionTime: result.executionTime });
                this.saveToHistory(
                    this.props.query,
                    this.props.connection,
                    result.records || 0,
                    result.executionTime
                );
            }
        };

        this.props.executeQuery(1, this.state.readOnly, callback);
    }

    render() {
        const {columns,ids} = this.props;

        const rowCount = ids.count() + 1;
        const columnCount = columns.length;
        return (
            <div className="admindb-container" style={{display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)'}}>
                {/* Template Selector */}
                <div className="row" style={{marginBottom: '10px', flexShrink: 0}}>
                    <div className="col-md-6">
                        <label style={{marginRight: '10px'}}>Templates de Soporte:</label>
                        <select className="form-control input-sm" onChange={this.loadTemplate} style={{display: 'inline-block', width: 'auto'}}>
                            {Object.keys(QUERY_TEMPLATES).map(key => (
                                <option key={key} value={key}>
                                    {key === '' ? '-- Seleccione un template' : key.replace(/_/g, ' ')}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-6" style={{textAlign: 'right'}}>
                        <button
                            type="button"
                            className="btn btn-default btn-sm"
                            onClick={() => this.setState({ showHistory: !this.state.showHistory })}
                        >
                            <FontAwesome name="history" /> Historial ({this.state.queryHistory.length})
                        </button>
                    </div>
                </div>

                {/* History Panel */}
                {this.state.showHistory && (
                    <div className="row" style={{marginBottom: '10px', flexShrink: 0}}>
                        <div className="col-md-12">
                            <div style={{
                                border: '1px solid #ddd',
                                padding: '10px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                backgroundColor: '#f9f9f9'
                            }}>
                                <div style={{marginBottom: '10px'}}>
                                    <strong>Historial de Queries</strong>
                                    <button
                                        type="button"
                                        className="btn btn-default btn-xs pull-right"
                                        onClick={this.clearHistory}
                                    >
                                        <FontAwesome name="trash" /> Limpiar
                                    </button>
                                </div>
                                {this.state.queryHistory.length === 0 ? (
                                    <p style={{color: '#999'}}>No hay queries en el historial</p>
                                ) : (
                                    this.state.queryHistory.map((entry, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                                borderBottom: '1px solid #eee',
                                                padding: '5px 0',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => this.loadQueryFromHistory(entry.query)}
                                        >
                                            <div style={{fontSize: '11px', color: '#666'}}>
                                                {new Date(entry.timestamp).toLocaleString()} |
                                                {entry.connection.toUpperCase()} |
                                                {entry.records} registros |
                                                {entry.executionTime}ms
                                            </div>
                                            <div style={{
                                                fontSize: '12px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {entry.query.substring(0, 100)}...
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Query Editor */}
                <div className="row" style={{flexShrink: 0}}>
                    <div className="col-md-12">
                        <AceEditor
                            width={'100%'}
                            height={`${this.state.editorHeight}px`}
                            mode="sql"
                            theme="github"
                            showPrintMargin={false}
                            value={this.props.query}
                            onChange={(v) => this.props.updateInputs('query',v)}
                            name="sql_editor"
                            editorProps={{$blockScrolling: Infinity}}
                            fontSize={14}
                            highlightActiveLine={true}
                            wrapEnabled={false}
                            setOptions={{
                                enableBasicAutocompletion: true,
                                enableLiveAutocompletion: false,
                                enableSnippets: false,
                                showLineNumbers: true,
                                tabSize: 4,
                                useWorker: false
                            }}
                        />
                    </div>
                </div>

                {/* Resizer Handle */}
                <div
                    className="row"
                    style={{
                        flexShrink: 0,
                        height: '8px',
                        backgroundColor: this.state.isResizing ? '#007bff' : '#e0e0e0',
                        cursor: 'ns-resize',
                        borderTop: '1px solid #ccc',
                        borderBottom: '1px solid #ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: this.state.isResizing ? 'none' : 'background-color 0.2s',
                        userSelect: 'none'
                    }}
                    onMouseDown={this.handleMouseDown}
                >
                    <div style={{
                        width: '40px',
                        height: '3px',
                        backgroundColor: this.state.isResizing ? '#fff' : '#999',
                        borderRadius: '2px'
                    }}></div>
                </div>

                {/* Controls Row */}
                <div className="row" style={{flexShrink: 0}}>
                    <div className="col-md-2" style={{padding:'10px'}}>
                        <select className="form-control input-sm" onChange={(e) => this.props.updateInputs('connection', e.target.value)} value={this.props.connection}>
                            <option value="mysql">MYSQL</option>
                            <option value="oracle">ORACLE</option>
                        </select>
                    </div>
                    <div className="col-md-4" style={{padding:'10px'}}>
                        <label style={{fontWeight: 'normal', marginBottom: '0'}}>
                            <input
                                type="checkbox"
                                checked={this.state.readOnly}
                                onChange={(e) => this.setState({ readOnly: e.target.checked })}
                                style={{marginRight: '5px'}}
                            />
                            Solo Lectura (READ-ONLY)
                        </label>
                    </div>
                    <div className="col-md-6" style={{padding:'10px',textAlign:'right'}}>
                        {this.state.executionTime > 0 && (
                            <span style={{marginRight: '15px', color: '#666'}}>
                                <FontAwesome name="clock-o" /> {this.state.executionTime}ms
                            </span>
                        )}
                        <button type="button" className="btn btn-sm btn-default" onClick={this.onClick}>
                            <FontAwesome name="play" /> Ejecutar
                        </button>
                    </div>
                </div>

                {/* Export Controls */}
                {this.props.records > 0 && (
                    <div className="row" style={{marginTop: '10px', marginBottom: '10px', flexShrink: 0}}>
                        <div className="col-md-12" style={{textAlign: 'right'}}>
                            <button
                                type="button"
                                className="btn btn-default btn-sm"
                                onClick={this.exportToCSV}
                                style={{marginRight: '5px'}}
                            >
                                <FontAwesome name="download" /> Exportar CSV
                            </button>
                            <button
                                type="button"
                                className="btn btn-default btn-sm"
                                onClick={this.copyToClipboard}
                            >
                                <FontAwesome name="clipboard" /> Copiar
                            </button>
                        </div>
                    </div>
                )}

                {/* Results Grid - Flex grow to fill available space */}
                <div className="row" style={{flex: 1, minHeight: 0}}>
                    <div className="col-md-12" style={{height: '100%'}}>
                        <div className="datawrapper" style={{height: '100%'}}>
                            {
                                (this.props.records > 0 &&
                                    <AutoSizer>
                                        {({width, height}) => (
                                            <MultiGrid
                                                ref={this.multiGridRef}
                                                cellRenderer={this.cellRenderer}
                                                columnWidth={this.getColumnWidth}
                                                columnCount={columnCount}
                                                fixedColumnCount={columns.filter(x => x.FIXED).length}
                                                fixedRowCount={1}
                                                height={height}
                                                rowHeight={20}
                                                rowCount={rowCount}
                                                width={width}
                                                hideTopRightGridScrollbar
                                                hideBottomLeftGridScrollbar
                                            />
                                        )}
                                    </AutoSizer>
                                )
                            }
                        </div>
                    </div>
                </div>

                {/* Pagination */}
                <div className="row" style={{flexShrink: 0, marginTop: '10px'}}>
                    <div className="col-md-12" style={{textAlign: 'right'}}>
                        <Pagination
                            activePage={ this.props.page }
                            itemsCountPerPage={ this.props.take }
                            totalItemsCount={ this.props.records }
                            pageRangeDisplayed={ 10 }
                            onChange={(pg) => this.props.executeQuery(pg, this.state.readOnly, () => {
                                if (this.multiGridRef.current) {
                                    this.multiGridRef.current.forceUpdateGrids()
                                }
                            })}
                            firstPageText={ <FontAwesome name="angle-double-left" /> }
                            prevPageText={ <FontAwesome name="angle-left" /> }
                            nextPageText={ <FontAwesome name="angle-right" /> }
                            lastPageText={ <FontAwesome name="angle-double-right" /> }
                        />
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    return {
        ids: state.admindb.get('ids'),
        records: state.admindb.get('records'),
        data: state.admindb.get('data'),
        columns: state.admindb.get('columns'),
        query: state.admindb.get('query'),
        connection: state.admindb.get('connection'),
        take: state.admindb.get('take'),
        page: state.admindb.get('page'),
    }
}

const mapDispatchToProps = {
    executeQuery,
    updateInputs,
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminDB)


// WEBPACK FOOTER //
// ./src/containers/AdminDB.jsx